<script setup lang="ts">
import { ArrowRight } from 'lucide-vue-next'
import ConfigSection from '@/components/ConfigSection.vue'
import type { NotificationConfig } from '@/composables/merchantNotificationConfigs'

const { t } = useI18n()
const {
  fetchNotificationConfigs,
  defaultClaimRewardNotification,
  multiselectNotificationConfigsStoresFormatted,
  updateMultiselectStores,
  handleRemoveStore,
  canEditRewards
} = useMerchantNotificationConfigs()
const router = useRouter()
defineProps<{
  configs: any[]
}>()

onMounted(async () => {
  updateMultiselectStores('claim-reward')
  const notifications = await fetchNotificationConfigs()
  const merchantdefaultClaimRewardNotification = notifications?.find(
    (notification) => notification.type_slug === 'claim-reward'
  )
  if (merchantdefaultClaimRewardNotification) {
    defaultClaimRewardNotification.value = merchantdefaultClaimRewardNotification
    defaultClaimRewardNotification.value.message_channel_slug =
      merchantdefaultClaimRewardNotification.message_channel.slug
    delete defaultClaimRewardNotification.value.message_channel
  }
  emit('update:notification', defaultClaimRewardNotification.value)
})

const redirectToRewards = () => {
  router.push('/loyalty')
}

const emit = defineEmits<{
  'update:notification': [notification: NotificationConfig]
}>()

watch(
  () => defaultClaimRewardNotification.value.is_active,
  () => {
    emit('update:notification', defaultClaimRewardNotification.value)
  }
)
</script>

<template>
  <ConfigSection :configs="configs">
    <template #content>
      <BaseDisclosure
        is-default-open
        :title="t('pages.marketplace.tabletsPage.tabletConfig.disclosure.title')"
        :subtitle="t('pages.marketplace.tabletsPage.tabletConfig.disclosure.subtitle')"
      >
        <div
          class="flex w-full flex-col justify-between gap-5 p-5"
          :class="{ 'pb-3': defaultClaimRewardNotification.is_active }"
        >
          <BaseTip severity="info">
            <template #text>
              <div class="flex w-full justify-between">
                <span>
                  {{ t('pages.marketplace.tabletsPage.tabletConfig.tooltip.subtitle') }}
                </span>
                <Button variant="link" @click="redirectToRewards">
                  {{ t('pages.marketplace.tabletsPage.tabletConfig.tooltip.button') }}
                  <ArrowRight />
                </Button>
              </div>
            </template>
          </BaseTip>
          <div class="flex justify-between">
            <label for="loyaltySubscriptionNotification" class="flex items-center text-sm font-medium text-foreground">
              <span class="mr-2.5 flex aspect-square h-8 items-center justify-center rounded-sm bg-primary/5">
                <BaseIcon name="HeroChatBubbleLeftIconOutline" class="h-5 w-5 text-primary" />
              </span>
              {{ t('pages.loyaltyPage.tabs.notifications.sendSmsAtSubscription.label.smsOnly') }}
            </label>
            <BaseSwitch
              id="loyaltySubscriptionNotification"
              v-model="defaultClaimRewardNotification.is_active"
              :label="defaultClaimRewardNotification.is_active ? t('global.words.enabled') : t('global.words.disabled')"
              :font-color="defaultClaimRewardNotification.is_active ? 'green' : 'red'"
            />
          </div>
          <div v-if="defaultClaimRewardNotification.is_active">
            <label class="text-sm font-medium text-foreground">
              {{ t('pages.loyaltyPage.tabs.notifications.sendSmsAtSubscription.label.stores') }}
            </label>
            <div class="w-1/2 pr-3">
              <BaseMultiSelect
                v-model="defaultClaimRewardNotification.store_ids"
                :disabled="!canEditRewards"
                :options="multiselectNotificationConfigsStoresFormatted"
                display="chip"
                :max-selected-labels="3"
                :selected-items-label="
                  t('global.multiselect.stores.storesSelected', {
                    count: defaultClaimRewardNotification.store_ids.length
                  })
                "
                fluid
                filter
              >
                <template #chip="{ value, option }">
                  <BaseChip
                    :text="option.label"
                    :removable="!option.disabled"
                    @click="$event.stopPropagation()"
                    @remove="handleRemoveStore(defaultClaimRewardNotification, value)"
                  />
                </template>
              </BaseMultiSelect>
            </div>
          </div>
        </div>
      </BaseDisclosure>
    </template>
  </ConfigSection>
</template>
